import { useEffect, useState } from "react";
import { CalendarCheck2, Loader2, MapPin, Navigation, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { apiGet, apiPost } from "@/lib/apiAdmin";
import { CopyUrl } from "./JarvisSettingsMore";
import { HUD } from "./theme";
import { fmt12 } from "@/lib/time";

const inputCls = `w-full h-10 rounded-xl px-3 text-sm border ${HUD.input}`;

const ago = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/Phoenix", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "never");

const GoogleHealth = () => {
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => apiGet("/admin/jarvis/google/health").then(setH).catch(() => {});
  useEffect(() => { load(); }, []);
  const run = (k, fn) => { setBusy(k); fn().then(load).catch((e) => toast.error(e?.response?.data?.detail || "Failed")).finally(() => setBusy("")); };
  if (!h) return null;
  const tone = h.healthy ? "border-emerald-400/30 text-emerald-300" : h.connected ? "border-amber-400/30 text-amber-300" : "border-rose-400/30 text-rose-300";
  return (
    <div className={`rounded-xl border p-3 space-y-2 ${tone}`} data-testid="jarvis-google-health">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium" data-testid="jarvis-google-health-status">
          Sync health: {h.healthy ? "All good" : h.connected ? "Needs attention" : "Not connected"}
          <span className="text-gray-400 font-normal"> · {h.synced}/{h.upcoming} upcoming bookings in Google · live push {h.channel_active ? "on" : "off"}</span>
        </p>
        <div className="flex gap-2">
          {h.connected && <button onClick={() => run("full", async () => { const r = await apiPost("/admin/jarvis/google/sync", {}); toast.success(`Checked ${r.report?.reconcile?.checked ?? 0}, repaired ${r.report?.reconcile?.repaired ?? 0}`); })} className={`${HUD.ghost} h-8 px-3 text-xs flex items-center gap-1`} data-testid="jarvis-google-full-sync">{busy === "full" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Full check</button>}
          <button onClick={() => run("alert", async () => { await apiPost("/admin/jarvis/google/test-alert", {}); toast.success("Test alert sent"); })} className={`${HUD.ghost} h-8 px-3 text-xs`} data-testid="jarvis-google-test-alert">{busy === "alert" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Test alert"}</button>
        </div>
      </div>
      {h.problems.length > 0 && (
        <ul className="text-xs space-y-1 list-disc pl-4" data-testid="jarvis-google-problems">{h.problems.map((p) => <li key={p}>{p}</li>)}</ul>
      )}
      <p className="text-[11px] text-gray-500">Last Google change pulled {ago(h.last_pull_at)} · last push notice {ago(h.last_push_at)} · last full check {ago(h.last_reconcile_at)}{h.last_error_at ? ` · last error ${ago(h.last_error_at)}` : ""}</p>
    </div>
  );
};

export const GoogleSection = ({ f, setF, s, keys, setKeys }) => {
  const [busy, setBusy] = useState("");
  const run = async (k, fn) => {
    setBusy(k);
    try { await fn(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };
  const connect = () => run("connect", async () => { window.location.href = (await apiGet("/admin/jarvis/google/auth-url")).url; });
  return (
    <div className={`${HUD.panel} p-5 space-y-4`} data-testid="jarvis-google-section">
      <div>
        <h3 className="text-white font-['Outfit'] text-lg">Google: Calendar, Gmail & Business reviews</h3>
        <p className="text-xs text-gray-400 mt-1">One Google connection lets JARVIS sync your calendar both ways, read your Gmail and send emails you approve, and sync & reply to your Google reviews. Already connected before? Press Connect again so Google asks for the new Gmail + Business permissions.</p>
        <details className="mt-2 text-xs text-gray-400" data-testid="jarvis-google-setup-guide">
          <summary className="cursor-pointer text-cyan-300">Google Cloud Console setup (5 min)</summary>
          <ol className="list-decimal pl-5 mt-2 space-y-1">
            <li>console.cloud.google.com → create/select a project.</li>
            <li>APIs & Services → Library → enable <b>Google Calendar API</b>, <b>Gmail API</b>, <b>My Business Account Management API</b>, <b>My Business Business Information API</b> and <b>Google My Business API</b>.</li>
            <li>OAuth consent screen → External → add your Gmail as a <b>Test user</b> (or Publish the app so the connection doesn't expire after 7 days).</li>
            <li>Credentials → Create OAuth client ID → Web application → Authorized redirect URI: <code className="text-cyan-200">{s.google_redirect_uri}</code></li>
            <li>Paste the Client ID + Secret below, Save, then Connect Google.</li>
            <li>Business reviews: Google must approve Business Profile API access for your project — request it at developers.google.com/my-business (Prerequisites → access request form). Until approved, the site uses your 5 newest reviews via Places.</li>
          </ol>
        </details>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="space-y-1.5 block"><span className={HUD.label}>Google OAuth Client ID</span>
          <input value={f.google_client_id || ""} onChange={(e) => setF({ ...f, google_client_id: e.target.value })} data-testid="jarvis-google-client-id" className={inputCls} />
        </label>
        <label className="space-y-1.5 block"><span className={HUD.label}>Google OAuth Client Secret</span>
          <input type="password" autoComplete="off" value={keys.google_client_secret || ""} onChange={(e) => setKeys({ ...keys, google_client_secret: e.target.value })} placeholder={s.google_client_secret_hint || "Paste secret"} data-testid="jarvis-google-client-secret" className={inputCls} />
        </label>
      </div>
      <CopyUrl label="Authorized redirect URI (paste in Google Cloud)" url={s.google_redirect_uri} testid="jarvis-google-redirect" help="Google Cloud → APIs & Services → enable Google Calendar API → OAuth consent screen (External, add your Gmail as test user) → Credentials → OAuth client ID (Web) → add this redirect URI. Save here, then Connect." />
      <div className="flex flex-wrap items-center gap-2">
        {s.google_connected ? (
          <>
            <span className="text-sm text-emerald-300 flex items-center gap-2" data-testid="jarvis-google-connected"><CalendarCheck2 className="w-4 h-4" /> Connected {s.google_email}</span>
            <button onClick={() => run("sync", async () => toast.success(`Synced ${(await apiPost("/admin/jarvis/google/sync", {})).synced} booking(s)`))} data-testid="jarvis-google-sync" className={`${HUD.ghost} h-9 px-3 text-xs flex items-center gap-1`}>
              {busy === "sync" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Sync now
            </button>
            <button onClick={() => run("dc", async () => { await apiPost("/admin/jarvis/google/disconnect", {}); toast.success("Disconnected — reload to refresh"); })} className="h-9 px-3 rounded-full text-xs text-red-300 hover:bg-red-400/10" data-testid="jarvis-google-disconnect">Disconnect</button>
          </>
        ) : (
          <button onClick={connect} disabled={!s.google_client_id || !s.google_client_secret_set} data-testid="jarvis-google-connect" className={`${HUD.btn} h-10 px-5 text-sm disabled:opacity-40`}>
            {busy === "connect" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connect Google Calendar"}
          </button>
        )}
        {!s.google_connected && (!s.google_client_id || !s.google_client_secret_set) && <span className="text-[11px] text-gray-500">Save Client ID + Secret first</span>}
      </div>
      <GoogleHealth />
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-cyan-400/10 p-3">
        <div>
          <p className="text-sm text-white">Auto-convert Gmail jobs into bookings + invoices</p>
          <p className="text-xs text-gray-500">Checks your linked calendars every 15 min. Personal entries (dentist, “Off”) are ignored.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => run("imp", async () => { const r = await apiPost("/admin/jarvis/import-calendar-jobs", {}); toast.success(`Imported ${r.imported} new job(s)`); })} data-testid="jarvis-import-now" className={`${HUD.ghost} h-9 px-3 text-xs`}>
            {busy === "imp" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Import now"}
          </button>
          <Switch checked={f.auto_import_jobs !== false} onCheckedChange={(v) => setF({ ...f, auto_import_jobs: v })} data-testid="jarvis-auto-import" />
        </div>
      </div>
      <label className="space-y-1.5 block"><span className={HUD.label}>Route start (home base)</span>
        <input value={f.route_start || ""} onChange={(e) => setF({ ...f, route_start: e.target.value })} placeholder="Maricopa, AZ" data-testid="jarvis-route-start" className={inputCls} />
      </label>
    </div>
  );
};

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Phoenix" });

export const RouteCard = () => {
  const [date, setDate] = useState(today());
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    apiGet("/admin/jarvis/route", { date }).then(setR).catch(() => setR(null)).finally(() => setLoading(false));
  }, [date]);
  return (
    <div className={`${HUD.panel} p-5 space-y-3`} data-testid="jarvis-route">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className={HUD.label}>Route plan · driving order</p>
        <div className="flex items-center gap-2">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="jarvis-route-date" className={`h-9 rounded-xl px-3 text-xs border ${HUD.input}`} />
          {r?.maps_url && <a href={r.maps_url} target="_blank" rel="noreferrer" data-testid="jarvis-route-maps" className={`${HUD.btn} h-9 px-4 text-xs flex items-center gap-1`}><Navigation className="w-3.5 h-3.5" /> Google Maps</a>}
        </div>
      </div>
      {loading ? <Loader2 className="w-5 h-5 animate-spin text-cyan-300" /> : !r?.stops?.length ? (
        <p className="text-sm text-gray-500">No jobs with addresses on this day.</p>
      ) : (
        <>
          <p className="text-xs text-gray-400">From {r.start} · ~{r.total_drive_min} min driving · {r.total_miles} mi</p>
          <ol className="space-y-2">
            {r.stops.map((x, i) => (
              <li key={i} className="flex gap-3" data-testid={`jarvis-route-stop-${i}`}>
                <span className="w-6 h-6 rounded-full bg-cyan-400/15 text-cyan-200 text-xs flex items-center justify-center shrink-0">{i + 1}</span>
                <div className="min-w-0">
                  <p className="text-sm text-white truncate">{fmt12(x.time)} · {x.label}</p>
                  <p className="text-xs text-gray-400 flex items-center gap-1"><MapPin className="w-3 h-3" /> {x.address}{x.drive_min != null && ` · ${x.drive_min} min drive`}</p>
                  {x.warning && <p className="text-xs text-amber-300">{x.warning}</p>}
                </div>
              </li>
            ))}
          </ol>
        </>
      )}
      {r?.missing_address?.length > 0 && <p className="text-[11px] text-amber-300/80">{r.missing_address.length} booking(s) have no address — ask JARVIS to add one.</p>}
    </div>
  );
};
